import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { RiFileExcel2Line } from 'react-icons/ri'
import { SlRefresh } from 'react-icons/sl'
import * as XLSX from 'xlsx'
import useOdulOneriCall from '../../hooks/useOdulOneriCall'
import {
  STATUS_FILTER_OPTIONS,
  calcSlaDelay,
  formatIsoDateTime,
  getAssigneeLabel,
  getBoardDecisionLabel,
  getManagerDecisionLabel,
  getStatusLabel,
  isClosedStatus,
  matchesStatusFilter,
} from '../../helper/odulOneriTracking'
import {
  formatWeightedScore,
  resolveBoardGroupScore,
} from '../../helper/odulOneriScoring'
import {
  formatDateDMY,
  formatEur,
  getImplementationMethodLabel,
} from '../../helper/odulOneriImplementation'
import { OPEX_DECISION } from '../../helper/odulOneriOpexReasons'
import { RECORD_STATUS, isImplementationPhase } from '../../helper/odulOneriStatus'

const DELAY_FILTER_OPTIONS = [
  { value: 'all', label: 'Tümü' },
  { value: 'overdue', label: 'SLA gecikmiş' },
  { value: 'normal', label: 'Zamanında' },
]

const INITIAL_FILTERS = {
  facility: 'all',
  department: 'all',
  dateFrom: '',
  dateTo: '',
  status: 'all',
  delay: 'all',
}

const getOpexDecisionLabel = (opexDecision) => {
  if (!opexDecision?.result) return '—'
  if (opexDecision.result === OPEX_DECISION.FORWARD) return 'Yönlendirildi'
  if (opexDecision.result === OPEX_DECISION.REJECT) return 'Öneri değil'
  return opexDecision.result
}

const mapIkReportRow = (record, impl) => {
  const sla = calcSlaDelay(record)
  const md = record.managerDecision
  const bd = record.boardDecision
  const od = record.opexDecision
  const facility =
    record.applicant?.location || impl?.facility || record.facility || '—'
  const dueDate = impl?.plan?.dueDate || ''
  const score = resolveBoardGroupScore(bd, bd?.topic || record.topic)
  const closed = isClosedStatus(record.status)

  return {
    id: record.id,
    recordNumber: record.recordNumber || '—',
    uygulamaNo: record.uygulamaNo || impl?.uygulamaNo || '—',
    createdAt: record.createdAt || '',
    createdAtLabel: formatIsoDateTime(record.createdAt),
    closedAtLabel: formatIsoDateTime(record.closedAt),
    applicantName: record.applicant?.fullName || '—',
    facility,
    relatedDepartmentName: record.relatedDepartmentName || '—',
    topic: record.topic || '—',
    status: record.status || '',
    statusLabel: getStatusLabel(record.status),
    assigneeLabel: closed ? '—' : getAssigneeLabel(record),
    opexDecisionLabel: getOpexDecisionLabel(od),
    managerDecisionLabel: md?.result ? getManagerDecisionLabel(md.result) : '—',
    methodLabel:
      getImplementationMethodLabel(impl?.method || md?.method || impl?.plan?.method) ||
      '—',
    leaderName: impl?.plan?.leaderName || '—',
    dueDateLabel: formatDateDMY(dueDate),
    actualBenefitArea: impl?.realization?.actualBenefitArea || bd?.topic || '—',
    boardDecisionLabel: getBoardDecisionLabel(bd),
    groupScore: score != null ? formatWeightedScore(score) : '—',
    estimatedCostLabel: formatEur(impl?.benefit?.estimatedCost),
    estimatedGainLabel: formatEur(impl?.benefit?.estimatedGain),
    isOverdue: sla.isOverdue,
    overdueDays: sla.overdueDays,
    slaLabel: sla.isOverdue ? `${sla.overdueDays} gün gecikme` : '—',
  }
}

const EXPORT_COLUMNS = [
  { key: 'recordNumber', header: 'Kayıt No' },
  { key: 'uygulamaNo', header: 'UY No' },
  { key: 'createdAtLabel', header: 'Başvuru' },
  { key: 'applicantName', header: 'Başvuran' },
  { key: 'facility', header: 'Tesis' },
  { key: 'relatedDepartmentName', header: 'Bölüm' },
  { key: 'topic', header: 'Konu' },
  { key: 'statusLabel', header: 'Durum' },
  { key: 'assigneeLabel', header: 'Güncel sorumlu' },
  { key: 'opexDecisionLabel', header: 'OPEX' },
  { key: 'managerDecisionLabel', header: 'Yönetici' },
  { key: 'methodLabel', header: 'Uygulama yolu' },
  { key: 'leaderName', header: 'Uygulama lideri' },
  { key: 'dueDateLabel', header: 'Termin' },
  { key: 'actualBenefitArea', header: 'Gerçekleşen fayda alanı' },
  { key: 'boardDecisionLabel', header: 'Kurul' },
  { key: 'groupScore', header: 'Kurul puan' },
  { key: 'estimatedCostLabel', header: 'Tahmini maliyet (€)' },
  { key: 'estimatedGainLabel', header: 'Tahmini kazanç (€)' },
  { key: 'slaLabel', header: 'SLA' },
  { key: 'closedAtLabel', header: 'Kapanış' },
]

const IKTakipRapor = () => {
  const {
    fetchAllOdulOneriRecords,
    fetchImplementations,
    fetchMailFailures,
    resolveMailFailure,
    fetchDepartments,
    processSlaReminders,
  } = useOdulOneriCall()

  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [mailFailures, setMailFailures] = useState([])
  const [departments, setDepartments] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [resolvingId, setResolvingId] = useState('')

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [records, implementations, failures, depts] = await Promise.all([
        fetchAllOdulOneriRecords(),
        fetchImplementations(),
        fetchMailFailures(),
        fetchDepartments(),
      ])
      const implById = Object.fromEntries(
        (implementations || []).map((i) => [i.id, i])
      )
      setRows(
        (records || []).map((record) =>
          mapIkReportRow(
            record,
            record.implementationId ? implById[record.implementationId] : null
          )
        )
      )
      setMailFailures(failures)
      if (depts?.length) setDepartments(depts)
    } finally {
      setLoading(false)
    }
  }, [
    fetchAllOdulOneriRecords,
    fetchImplementations,
    fetchMailFailures,
    fetchDepartments,
  ])

  useEffect(() => {
    processSlaReminders().catch((err) => console.log('processSlaReminders:', err))
    loadData()
  }, [loadData, processSlaReminders])

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  const facilityOptions = useMemo(() => {
    return [...new Set(rows.map((r) => r.facility).filter((f) => f && f !== '—'))].sort(
      (a, b) => a.localeCompare(b, 'tr')
    )
  }, [rows])

  const departmentOptions = useMemo(() => {
    const fromConfig = departments.map((d) => d.name).filter(Boolean)
    const fromRecords = rows
      .map((r) => r.relatedDepartmentName)
      .filter((n) => n && n !== '—')
    return [...new Set([...fromConfig, ...fromRecords])].sort((a, b) =>
      a.localeCompare(b, 'tr')
    )
  }, [departments, rows])

  const tableRows = useMemo(() => {
    return rows.filter((row) => {
      if (filters.facility !== 'all' && row.facility !== filters.facility) return false
      if (
        filters.department !== 'all' &&
        row.relatedDepartmentName !== filters.department
      ) {
        return false
      }
      if (!matchesStatusFilter(row.status, filters.status)) return false
      if (filters.delay === 'overdue' && !row.isOverdue) return false
      if (filters.delay === 'normal' && row.isOverdue) return false
      if (filters.dateFrom) {
        const from = new Date(filters.dateFrom).getTime()
        const created = new Date(row.createdAt || 0).getTime()
        if (created < from) return false
      }
      if (filters.dateTo) {
        const to = new Date(filters.dateTo).getTime()
        const created = new Date(row.createdAt || 0).getTime()
        if (created > to + 86400000) return false
      }
      return true
    })
  }, [rows, filters])

  const summary = useMemo(() => {
    let inImpl = 0
    let awarded = 0
    let thanks = 0
    tableRows.forEach((r) => {
      if (isImplementationPhase(r.status) || r.status === RECORD_STATUS.AWARD_POOL) {
        inImpl += 1
      }
      if (r.status === RECORD_STATUS.CLOSED_AWARDED) awarded += 1
      if (r.status === RECORD_STATUS.CLOSED_THANKS) thanks += 1
    })
    return { total: tableRows.length, inImpl, awarded, thanks }
  }, [tableRows])

  const columns = useMemo(
    () => [
      { field: 'recordNumber', headerName: 'Kayıt No', minWidth: 130, flex: 0.9 },
      { field: 'uygulamaNo', headerName: 'UY No', minWidth: 120, flex: 0.8 },
      { field: 'createdAtLabel', headerName: 'Başvuru', minWidth: 140, flex: 0.9 },
      { field: 'applicantName', headerName: 'Başvuran', minWidth: 130, flex: 0.9 },
      { field: 'facility', headerName: 'Tesis', minWidth: 110, flex: 0.7 },
      { field: 'relatedDepartmentName', headerName: 'Bölüm', minWidth: 140, flex: 1 },
      { field: 'topic', headerName: 'Konu', minWidth: 180, flex: 1.2 },
      { field: 'statusLabel', headerName: 'Durum', minWidth: 170, flex: 1.1 },
      { field: 'opexDecisionLabel', headerName: 'OPEX', minWidth: 110, flex: 0.7 },
      { field: 'managerDecisionLabel', headerName: 'Yönetici', minWidth: 90, flex: 0.6 },
      { field: 'methodLabel', headerName: 'Uygulama yolu', minWidth: 120, flex: 0.8 },
      { field: 'leaderName', headerName: 'Lider', minWidth: 120, flex: 0.8 },
      { field: 'dueDateLabel', headerName: 'Termin', minWidth: 100, flex: 0.6 },
      {
        field: 'actualBenefitArea',
        headerName: 'Fayda alanı',
        minWidth: 160,
        flex: 1.1,
      },
      { field: 'boardDecisionLabel', headerName: 'Kurul', minWidth: 90, flex: 0.6 },
      { field: 'groupScore', headerName: 'Kurul puan', minWidth: 90, flex: 0.6 },
      {
        field: 'estimatedCostLabel',
        headerName: 'Maliyet €',
        minWidth: 100,
        flex: 0.7,
      },
      {
        field: 'estimatedGainLabel',
        headerName: 'Kazanç €',
        minWidth: 100,
        flex: 0.7,
      },
      { field: 'assigneeLabel', headerName: 'Sorumlu', minWidth: 110, flex: 0.7 },
      { field: 'slaLabel', headerName: 'SLA', minWidth: 100, flex: 0.6 },
      { field: 'closedAtLabel', headerName: 'Kapanış', minWidth: 140, flex: 0.9 },
    ],
    []
  )

  const columnVisibilityModel = useMemo(
    () => ({
      estimatedCostLabel: false,
      estimatedGainLabel: false,
      assigneeLabel: false,
      slaLabel: false,
    }),
    []
  )

  const handleExportExcel = () => {
    const headers = EXPORT_COLUMNS.map((c) => c.header)
    const lines = [
      headers,
      ...tableRows.map((row) =>
        EXPORT_COLUMNS.map((c) => {
          const v = row[c.key]
          return v == null || v === '—' ? '' : v
        })
      ),
    ]
    const worksheet = XLSX.utils.aoa_to_sheet(lines)
    worksheet['!cols'] = headers.map((header, colIndex) => ({
      wch: Math.min(
        48,
        Math.max(
          header.length + 2,
          ...lines.slice(1).map((r) => String(r[colIndex] ?? '').length + 1)
        )
      ),
    }))
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'IK Takip')
    const today = new Date().toISOString().slice(0, 10)
    XLSX.writeFile(workbook, `Bonna_Odul_Oneri_IK_Takip_${today}.xlsx`)
  }

  const handleResolveMail = async (failureId) => {
    setResolvingId(failureId)
    try {
      await resolveMailFailure(failureId)
      setMailFailures((prev) => prev.filter((item) => item.id !== failureId))
    } finally {
      setResolvingId('')
    }
  }

  const statCardSx = {
    flex: '1 1 120px',
    p: 1.5,
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'background.paper',
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 'none',
        px: { xs: 1.5, sm: 2 },
        py: 2,
        mt: 10,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 2,
        }}
      >
        <Typography fontWeight={700}>Ödül Öneri — İK Takip</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<RiFileExcel2Line size={16} />}
            onClick={handleExportExcel}
            disabled={loading || tableRows.length === 0}
            sx={{ textTransform: 'none' }}
          >
            Excel&apos;e aktar
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SlRefresh size={16} />}
            onClick={() => {
              setFilters(INITIAL_FILTERS)
              loadData()
            }}
            sx={{ textTransform: 'none' }}
          >
            Yenile
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={statCardSx}>
          <Typography variant="caption" color="text.secondary">
            Toplam
          </Typography>
          <Typography fontWeight={700}>{summary.total}</Typography>
        </Box>
        <Box sx={statCardSx}>
          <Typography variant="caption" color="text.secondary">
            Uygulamada / havuz
          </Typography>
          <Typography fontWeight={700}>{summary.inImpl}</Typography>
        </Box>
        <Box sx={statCardSx}>
          <Typography variant="caption" color="text.secondary">
            Ödül
          </Typography>
          <Typography fontWeight={700}>{summary.awarded}</Typography>
        </Box>
        <Box sx={statCardSx}>
          <Typography variant="caption" color="text.secondary">
            Teşekkür
          </Typography>
          <Typography fontWeight={700}>{summary.thanks}</Typography>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(6, 1fr)',
            },
            gap: 2,
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel id="facility-filter">Tesis</InputLabel>
            <Select
              labelId="facility-filter"
              name="facility"
              label="Tesis"
              value={filters.facility}
              onChange={handleFilterChange}
            >
              <MenuItem value="all">Tümü</MenuItem>
              {facilityOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="dept-filter">Bölüm</InputLabel>
            <Select
              labelId="dept-filter"
              name="department"
              label="Bölüm"
              value={filters.department}
              onChange={handleFilterChange}
            >
              <MenuItem value="all">Tümü</MenuItem>
              {departmentOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="status-filter">Durum</InputLabel>
            <Select
              labelId="status-filter"
              name="status"
              label="Durum"
              value={filters.status}
              onChange={handleFilterChange}
            >
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="delay-filter">SLA</InputLabel>
            <Select
              labelId="delay-filter"
              name="delay"
              label="SLA"
              value={filters.delay}
              onChange={handleFilterChange}
            >
              {DELAY_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            name="dateFrom"
            label="Başlangıç"
            InputLabelProps={{ shrink: true }}
            value={filters.dateFrom}
            onChange={handleFilterChange}
          />
          <TextField
            size="small"
            type="date"
            name="dateTo"
            label="Bitiş"
            InputLabelProps={{ shrink: true }}
            value={filters.dateTo}
            onChange={handleFilterChange}
          />
        </Box>
      </Paper>

      <Box sx={{ width: '100%', overflowX: 'auto', mb: 3 }}>
        <DataGrid
          loading={loading}
          rows={tableRows}
          columns={columns}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            columns: { columnVisibilityModel },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          sx={{ minWidth: 1200, border: 1, borderColor: 'divider' }}
        />
      </Box>

      {mailFailures.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Gönderilemeyen e-postalar
          </Typography>
          <List dense>
            {mailFailures.map((f) => (
              <ListItem
                key={f.id}
                secondaryAction={
                  <Button
                    size="small"
                    disabled={resolvingId === f.id}
                    onClick={() => handleResolveMail(f.id)}
                    sx={{ textTransform: 'none' }}
                  >
                    Çözüldü
                  </Button>
                }
              >
                <ListItemText
                  primary={`${f.subject || '—'} → ${f.to || '—'}`}
                  secondary={f.error || f.createdAt}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {!loading && tableRows.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Filtrelere uyan kayıt yok.
        </Alert>
      )}
    </Box>
  )
}

export default IKTakipRapor
